import { cn } from "@/lib/utils";
import { ExternalLink, MapPin, Pencil, Repeat, Trash2 } from "lucide-react";
import { type CalEvent, formatTime, showAsDot } from "./types";

/** Extracted verbatim from client/src/pages/Calendar.tsx — no behavior change. */
export function EventCard({
  event,
  onEdit,
  onDelete,
  onRemoveFromMine,
  isOwnCalendar,
}: {
  event: CalEvent;
  onEdit?: (event: CalEvent) => void;
  onDelete?: (event: CalEvent) => void;
  onRemoveFromMine?: (event: CalEvent) => void;
  /** True only when viewing the signed-in user's OWN calendar (selectedEmail === "self"). */
  isOwnCalendar: boolean;
}) {
  const timeLabel = event.isAllDay
    ? "All day"
    : `${formatTime(event.start.dateTime)} – ${formatTime(event.end.dateTime)}`;

  const location = event.location?.displayName?.trim();
  const isRecurring = event.type === "occurrence" || event.type === "seriesMaster" || event.type === "exception";
  // Edit is offered only for events the viewer organizes on their OWN
  // calendar (Graph isOrganizer=true) — never for arbitrary Outlook events
  // created by someone else, and never edited "as if own" for events where
  // the viewer is merely an attendee (those get Remove/decline instead).
  // Graph's isOrganizer is relative to whichever mailbox is being VIEWED, not
  // the signed-in caller — so it must also be gated on isOwnCalendar, or a
  // manager viewing someone else's calendar sees live-looking Edit/Delete
  // buttons that always 404 server-side (own-calendar-only enforcement).
  const canEdit = isOwnCalendar && event.isOrganizer === true;
  const canRemove = isOwnCalendar && event.isOrganizer === false;

  return (
    <div
      className={cn(
        "group relative rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm transition-shadow hover:shadow-md",
        event.isAllDay && "border-dashed bg-muted/40"
      )}
    >
      <div className="flex items-start gap-2">
        <span
          className={cn(
            "mt-1.5 h-2 w-2 shrink-0 rounded-full",
            showAsDot(event.showAs)
          )}
          title={event.showAs ?? "free"}
        />
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug text-foreground truncate flex items-center gap-1">
            {event.subject || "(No title)"}
            {isRecurring && (
              <span title="Recurring event">
                <Repeat className="w-3 h-3 shrink-0 text-muted-foreground" />
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{timeLabel}</p>
          {location && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 truncate">
              <MapPin className="w-3 h-3 shrink-0" />
              {location}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {canEdit && onEdit && (
            <button
              onClick={() => onEdit(event)}
              className="text-muted-foreground hover:text-foreground"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {canEdit && onDelete && (
            <button
              onClick={() => onDelete(event)}
              className="text-muted-foreground hover:text-destructive"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {canRemove && onRemoveFromMine && (
            <button
              onClick={() => onRemoveFromMine(event)}
              className="text-muted-foreground hover:text-destructive"
              title="Remove from my calendar"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {event.webLink && (
            <a
              href={event.webLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
              title="Open in Outlook"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
